import { useEffect, useState, useCallback } from "react";
import Pusher from "pusher-js";
import { 
  BannerData, 
  BannerCloseData, 
  BannerState, 
  BannerAction,
  BannerTriggerEvent,
  BannerReplaceEvent,
  BannerCloseEvent,
  BannerPosition,
  PositionNumber,
  BannerError
} from "@/types/banner";
import { useAnalytics } from "@/lib/AnalyticsContext";
import { tryEnterPiP, isIOS, openInNewTab } from "@/lib/pip";

export const BANNER_CUSTOM_TRIGGER_EVENT = 'foove:banner-trigger';

// 位置マッピング定数
const POSITION_MAP: Record<PositionNumber, BannerPosition> = {
  1: { top: '10%', left: '10%' },      // 左上
  2: { top: '10%', left: '50%', transform: 'translateX(-50%)' },  // 中央上
  3: { top: '10%', right: '10%' },     // 右上
  4: { top: '50%', left: '10%', transform: 'translateY(-50%)' },  // 左中央
  5: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },  // 中央
  6: { top: '50%', right: '10%', transform: 'translateY(-50%)' },     // 右中央
};

// デバイス判定（既存パターンを踏襲）
const isMobile = (): boolean => {
  if (typeof navigator !== 'undefined' && navigator.userAgent.match(/iPhone|Android.+Mobile/)) {
    return true;
  }
  return false;
};


// 位置スタイル計算
const getPositionStyle = (position: number, margin?: BannerData['margin']): React.CSSProperties => {
  const positionKey = position as PositionNumber;
  const baseStyle = POSITION_MAP[positionKey];
  
  if (!baseStyle) {
    console.warn(`Invalid banner position: ${position}`);
    return POSITION_MAP[5]; // デフォルトは中央
  }

  return {
    position: 'absolute' as const,
    ...baseStyle,
    marginTop: margin?.top || 0,
    marginLeft: margin?.left || 0,
    marginRight: margin?.right || 0,
    marginBottom: margin?.bottom || 0,
  };
};

// バナーサイズ計算
const getBannerSize = (fullScreen: boolean): { width: number; height: number } => {
  if (fullScreen) {
    // モバイルフルサイズ時は画面幅に合わせて大きめに表示
    if (isMobile()) {
      return { width: 120, height: 120 }; // モバイルフルスクリーン時
    }
    return { width: 160, height: 160 }; // PCフルスクリーン時
  }
  return { width: 60, height: 60 }; // ミニプレイヤー
};

export const useBanner = (
  pusher: Pusher | null,
  episodeId: string,
  userId: string,
  fullScreen: boolean,
  testMode: boolean = false
) => {
  const [bannerState, setBannerState] = useState<BannerState>({
    banners: new Map(),
    positions: new Map(),
  });
  const [errors, setErrors] = useState<BannerError[]>([]);
  const [popupData, setPopupData] = useState<BannerData | null>(null);
  const analytics = useAnalytics();

  // エラーハンドリング
  const handleError = useCallback((error: BannerError) => {
    console.error('Banner error:', error);
    setErrors(prev => [...prev.slice(-9), error]); // 最新10件を保持
    
    // GAにエラーを送信
    analytics.sendBannerEvent(
      'banner-error' as BannerAction,
      episodeId,
      userId || 'anonymous',
      error.bannerId || 'unknown',
      'error'
    );
  }, [analytics, episodeId, userId]);

  // バナー表示処理
  const showBanner = useCallback((data: BannerData) => {
    console.log('[BANNER-DEBUG] showBanner called with data:', data);
    try {
      setBannerState(prevState => {
        console.log('[BANNER-DEBUG] Current banner state:', {
          banners: Array.from(prevState.banners.entries()),
          positions: Array.from(prevState.positions.entries())
        });
        
        const newBanners = new Map(prevState.banners);
        const newPositions = new Map(prevState.positions);

        // 同じIDのバナーが既に存在する場合は位置を更新
        const existingBanner = newBanners.get(data.id);
        if (existingBanner) {
          console.log('[BANNER-DEBUG] Updating existing banner position:', data.id, 'from', existingBanner.position, 'to', data.position);
          // 古い位置から削除
          if (newPositions.get(existingBanner.position) === data.id) {
            newPositions.delete(existingBanner.position);
          }
        }

        // 同じ位置に既存のバナーがある場合は削除（異なるIDの場合のみ）
        const existingBannerId = newPositions.get(data.position);
        if (existingBannerId && existingBannerId !== data.id) {
          console.log('[BANNER-DEBUG] Removing existing banner at position:', data.position, 'bannerId:', existingBannerId);
          newBanners.delete(existingBannerId);
        }

        // 新しいバナーを追加
        console.log('[BANNER-DEBUG] Adding new banner:', data.id, 'at position:', data.position);
        newBanners.set(data.id, data);
        newPositions.set(data.position, data.id);

        const newState = {
          banners: newBanners,
          positions: newPositions,
        };
        
        console.log('[BANNER-DEBUG] New banner state:', {
          banners: Array.from(newState.banners.entries()),
          positions: Array.from(newState.positions.entries())
        });

        return newState;
      });

      // GAイベント送信
      analytics.sendBannerEvent(
        'banner-trigger',
        episodeId,
        userId || 'anonymous',
        data.id,
        data.creativeId || 'unknown'
      );
    } catch (error) {
      handleError({
        type: 'PUSHER_ERROR',
        message: 'Failed to show banner',
        bannerId: data.id,
      });
    }
  }, [analytics, episodeId, userId, handleError]);

  // バナー更新処理
  const updateBanner = useCallback((data: BannerData) => {
    try {
      setBannerState(prevState => {
        const newBanners = new Map(prevState.banners);
        
        // 既存のバナーが存在する場合のみ更新
        if (newBanners.has(data.id)) {
          newBanners.set(data.id, data);
          
          // GAイベント送信
          analytics.sendBannerEvent(
            'banner-replace',
            episodeId,
            userId || 'anonymous',
            data.id,
            data.creativeId || 'unknown'
          );
          
          return {
            ...prevState,
            banners: newBanners,
          };
        }
        
        return prevState;
      });
    } catch (error) {
      handleError({
        type: 'PUSHER_ERROR',
        message: 'Failed to update banner',
        bannerId: data.id,
      });
    }
  }, [analytics, episodeId, userId, handleError]);

  // バナー閉じる処理
  const closeBanner = useCallback((data: BannerCloseData) => {
    try {
      setBannerState(prevState => {
        const newBanners = new Map(prevState.banners);
        const newPositions = new Map(prevState.positions);

        // バナーを削除
        if (newBanners.has(data.id)) {
          newBanners.delete(data.id);
          newPositions.delete(data.position);

          // GAイベント送信
          analytics.sendBannerEvent(
            'banner-close',
            episodeId,
            userId || 'anonymous',
            data.id,
            data.creativeId || 'unknown'
          );
        }

        return {
          banners: newBanners,
          positions: newPositions,
        };
      });
    } catch (error) {
      handleError({
        type: 'PUSHER_ERROR',
        message: 'Failed to close banner',
        bannerId: data.id,
      });
    }
  }, [analytics, episodeId, userId, handleError]);

  // ユーザーによるバナー閉じる処理
  const handleUserCloseBanner = useCallback((bannerId: string) => {
    console.log('[BANNER-DEBUG] handleUserCloseBanner called:', {
      bannerId,
      episodeId,
      userId,
      hasAnalytics: !!analytics
    });

    try {
      // まず現在のバナー情報を取得
      const currentBanner = bannerState.banners.get(bannerId);
      if (!currentBanner) {
        console.warn('[BANNER-DEBUG] Banner not found:', bannerId);
        return;
      }

      // GAイベント送信を先に実行（状態更新の前に）
      console.log('[BANNER-DEBUG] Sending GA event for banner close:', {
        action: 'banner-user-close',
        episodeId,
        userId: userId || 'anonymous',
        bannerId,
        creativeId: currentBanner.creativeId || 'unknown'
      });

      analytics.sendBannerEvent(
        'banner-user-close',
        episodeId,
        userId || 'anonymous',
        bannerId,
        currentBanner.creativeId || 'unknown'
      );

      // その後で状態を更新
      setBannerState(prevState => {
        const newBanners = new Map(prevState.banners);
        const newPositions = new Map(prevState.positions);

        newBanners.delete(bannerId);
        newPositions.delete(currentBanner.position);

        return {
          banners: newBanners,
          positions: newPositions,
        };
      });

      console.log('[BANNER-DEBUG] Banner closed successfully:', bannerId);
    } catch (error) {
      console.error('[BANNER-DEBUG] Error in handleUserCloseBanner:', error);
      handleError({
        type: 'ANALYTICS_ERROR',
        message: 'Failed to handle user close banner',
        bannerId,
      });
    }
  }, [analytics, episodeId, userId, handleError, bannerState.banners]);

  // ユーザーによるバナークリック処理
  const handleUserClickBanner = useCallback((
    bannerId: string,
    type: string,
    link?: string,
    clipText?: string
  ) => {
    try {
      const banner = bannerState.banners.get(bannerId);
      if (!banner) return;

      if (type === 'link' && link) {
        // PiPへ移行（購入導線で別タブへ遷移する前に視聴継続）
        // iOS では PiP を呼ぶと window.open がブロックされるためスキップ
        if (!isIOS()) {
          tryEnterPiP(episodeId);
        }
        // リンクを開く
        openInNewTab(link);
        
        // GAイベント送信
        analytics.sendBannerEvent(
          'banner-user-click',
          episodeId,
          userId,
          bannerId,
          banner.creativeId || 'unknown'
        );
      } else if (type === 'clip' && clipText) {
        console.log('[BANNER-DEBUG] Clip action:', clipText);
        
        // ポップアップを表示
        setPopupData(banner);
        
        // GAイベント送信（ポップアップ表示時）
        analytics.sendBannerEvent(
          'banner-user-clip',
          episodeId,
          userId || 'anonymous',
          bannerId,
          banner.creativeId || 'unknown'
        );
      }
    } catch (error) {
      handleError({
        type: 'ANALYTICS_ERROR',
        message: 'Failed to handle user click banner',
        bannerId,
      });
    }
  }, [bannerState.banners, analytics, episodeId, userId, handleError]);

  // カスタムイベントによるバナー表示
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleCustomTrigger = (event: Event) => {
      const customEvent = event as CustomEvent<any>;
      const { detail } = customEvent;

      if (!detail) {
        console.warn(`[BANNER-DEBUG] ${BANNER_CUSTOM_TRIGGER_EVENT} event received without detail`);
        return;
      }

      let bannerData: BannerData | null = null;

      if (typeof detail === 'string') {
        try {
          const parsed = JSON.parse(detail);
          bannerData = parsed?.bannerData ?? parsed ?? null;
        } catch (error) {
          console.error('[BANNER-DEBUG] Failed to parse custom trigger detail:', error);
          return;
        }
      } else if (detail.bannerData) {
        bannerData = detail.bannerData;
      } else {
        bannerData = detail as BannerData;
      }

      if (!bannerData) {
        console.warn('[BANNER-DEBUG] No bannerData found in custom trigger detail:', detail);
        return;
      }
      if (bannerData.episodeId && bannerData.episodeId !== episodeId) {
        console.warn('[BANNER-DEBUG] Custom event episodeId mismatch:', {
          expected: episodeId,
          received: bannerData.episodeId,
        });
        return;
      }

      showBanner({ ...bannerData, episodeId });
    };

    window.addEventListener(BANNER_CUSTOM_TRIGGER_EVENT, handleCustomTrigger);

    return () => {
      window.removeEventListener(BANNER_CUSTOM_TRIGGER_EVENT, handleCustomTrigger);
    };
  }, [showBanner]);

  // Pusherイベントリスナーの設定
  useEffect(() => {
    if (!pusher || !episodeId) return;

    const channelName = `${episodeId}-viewer-channel`;
    let channel = pusher.channel(channelName);
    
    if (!channel) {
      channel = pusher.subscribe(channelName);
    }

    // バナー表示イベント
    const triggerEventName = `${episodeId}-banner-trigger-event`;
    channel.bind(triggerEventName, (data: BannerTriggerEvent | any) => {
      console.log('[BANNER-DEBUG] Received trigger event:', data);
      
      let bannerData = null;
      
      // データが文字列の場合はパース
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          bannerData = parsed.bannerData;
        } catch (error) {
          console.error('[BANNER-DEBUG] Failed to parse trigger event data:', error);
          return;
        }
      } else if (data && data.bannerData) {
        bannerData = data.bannerData;
      }
      
      if (bannerData) {
        // テストフラグによるフィルタリング
        const isTestBanner =
          bannerData.test === true || bannerData.test === 'true';

        if (isTestBanner && !testMode) {
          console.log('[BANNER-DEBUG] Ignoring test banner (testMode disabled):', bannerData);
          return;
        }
        console.log('[BANNER-DEBUG] Showing banner:', bannerData);
        showBanner(bannerData);
      } else {
        console.warn('[BANNER-DEBUG] No bannerData found in trigger event:', data);
      }
    });

    // バナー更新イベント
    const replaceEventName = `${episodeId}-banner-replace-event`;
    channel.bind(replaceEventName, (data: BannerReplaceEvent | any) => {
      console.log('[BANNER-DEBUG] Received replace event:', data);
      
      let bannerData = null;
      
      // データが文字列の場合はパース
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          bannerData = parsed.bannerData;
        } catch (error) {
          console.error('[BANNER-DEBUG] Failed to parse replace event data:', error);
          return;
        }
      } else if (data && data.bannerData) {
        bannerData = data.bannerData;
      }
      
      if (bannerData) {
        // テストフラグによるフィルタリング
        const isTestBanner =
          bannerData.test === true || bannerData.test === 'true';

        if (isTestBanner && !testMode) {
          console.log('[BANNER-DEBUG] Ignoring test banner update (testMode disabled):', bannerData);
          return;
        }
        console.log('[BANNER-DEBUG] Updating banner:', bannerData);
        updateBanner(bannerData);
      } else {
        console.warn('[BANNER-DEBUG] No bannerData found in replace event:', data);
      }
    });

    // バナー閉じるイベント
    const closeEventName = `${episodeId}-banner-close-event`;
    channel.bind(closeEventName, (data: BannerCloseEvent | any) => {
      console.log('[BANNER-DEBUG] Received close event:', data);
      
      let bannerData = null;
      
      // データが文字列の場合はパース
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          bannerData = parsed.bannerData;
        } catch (error) {
          console.error('[BANNER-DEBUG] Failed to parse close event data:', error);
          return;
        }
      } else if (data && data.bannerData) {
        bannerData = data.bannerData;
      }
      
      if (bannerData) {
        console.log('[BANNER-DEBUG] Closing banner:', bannerData);
        closeBanner(bannerData);
      } else {
        console.warn('[BANNER-DEBUG] No bannerData found in close event:', data);
      }
    });

    // クリーンアップ
    return () => {
      if (channel) {
        channel.unbind(triggerEventName);
        channel.unbind(replaceEventName);
        channel.unbind(closeEventName);
      }
    };
  }, [pusher, episodeId, showBanner, updateBanner, closeBanner, testMode]);

  // ポップアップを閉じる関数
  const closePopup = useCallback(() => {
    setPopupData(null);
  }, []);

  // コピー処理関数
  const handleCopy = useCallback((clipText: string, bannerId: string, creativeId?: string) => {
    // GAイベント送信（コピー時）
    analytics.sendBannerEvent(
      'banner-user-copy',
      episodeId,
      userId || 'anonymous',
      bannerId,
      creativeId || 'unknown'
    );
  }, [analytics, episodeId, userId]);

  // バナーリストの取得
  const banners = Array.from(bannerState.banners.values());

  return {
    banners,
    bannerState,
    errors,
    handleUserCloseBanner,
    handleUserClickBanner,
    getPositionStyle,
    getBannerSize,
    isMobile: isMobile(),
    popupData,
    closePopup,
    handleCopy,
  };
}; 